import type { MeResponse } from '@qlk/shared';
import { Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { duongDanTrang } from '../lib/auth.js';
import { NHOM_MENU } from '../lib/modules.js';
import { dungMenu, hienMucMenu } from './menu.js';
import { OChonKyChung } from './OChonKyChung.js';
import { TimKiemChung } from './TimKiemChung.js';

export interface ThanhTrenProps {
  me: MeResponse;
  onMoNganKeo: () => void;
  /** Thanh bên đang thu gọn còn icon. */
  menuGon?: boolean;
  /** Bật / tắt thu gọn. Không truyền thì không hiện nút. */
  onDoiMenuGon?: () => void;
}

/**
 * Thanh trên: breadcrumb bên trái, tìm kiếm + ô chọn kỳ bên phải.
 *
 * Breadcrumb dựng **từ menu**, không từ trang: mỗi mục menu đã biết nhóm và
 * nhãn của nó, nên `/doanh-thu` ra `TÀI CHÍNH · Doanh thu` mà không trang nào
 * phải khai lại. Trang con (`/kenh/:id`) lấy mục cha có đường dẫn là tiền tố
 * dài nhất, rồi thêm "Chi tiết".
 *
 * ⚠️ Nhãn **"Dữ liệu mẫu"** nay thuộc thanh này (trước ở `PageHeader` của từng
 * trang). Nó chỉ hiện khi máy chủ chạy `YOUTUBE_MODE=mock` — xem
 * `lib/auth.duongDanTrang`, và test `lib/__tests__/duongDanTrang.test.tsx` canh
 * đúng chỗ này.
 */

/** Breadcrumb của đường dẫn hiện tại, dựa trên menu đã dựng. */
export function duongDanBreadcrumb(duongDan: string, menu: ReturnType<typeof dungMenu>): string[] {
  const tatCa = NHOM_MENU.flatMap((nhom) => menu[nhom].map((m) => ({ nhom, ...m })));

  const khopHan = tatCa.find((m) => m.to === duongDan);
  if (khopHan) return [khopHan.nhom, khopHan.label];

  /* Trang con: lấy mục cha có tiền tố dài nhất, trừ mục gốc `/`. */
  const cha = tatCa
    .filter((m) => m.to !== '/' && duongDan.startsWith(`${m.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];

  if (cha) return [cha.nhom, cha.label, 'Chi tiết'];
  return [];
}

export function ThanhTren({ me, onMoNganKeo, menuGon = false, onDoiMenuGon }: ThanhTrenProps) {
  const viTri = useLocation();
  const menu = dungMenu();

  /* Ẩn mục không có quyền trước khi dựng breadcrumb, để không hiện tên trang
     mà người này không mở được. */
  const menuThayDuoc = Object.fromEntries(
    NHOM_MENU.map((nhom) => [nhom, menu[nhom].filter((m) => hienMucMenu(me.permissions, m))]),
  ) as ReturnType<typeof dungMenu>;

  /*
   * Gắn nhãn chế độ dữ liệu vào **cuối** breadcrumb: nó nói về số liệu đang
   * xem, không phải về vị trí trong menu.
   *
   * `duongDanTrang('')` trả một phần tử rỗng ở live, và thêm nhãn chế độ ở
   * mock — lọc chuỗi rỗng đi là xong. Gọi qua hàm đó chứ không gõ nhãn vào đây:
   * test `duongDanTrang.test.tsx` quét mã nguồn, chặn mọi chỗ viết cứng nhãn.
   */
  const nhanCheDo = duongDanTrang('', me.youtubeMode).filter(Boolean);
  const duongDan = [...duongDanBreadcrumb(viTri.pathname, menuThayDuoc), ...nhanCheDo];

  return (
    <header className="sticky top-(--cao-dai) z-30 border-b border-vien bg-be-mat">
      <div className="flex min-h-(--cao-thanh-tren) flex-wrap items-center gap-3 px-4 py-2 md:px-7">
        <button
          type="button"
          onClick={onMoNganKeo}
          aria-label="Mở menu"
          className="vung-bam -ml-2 inline-flex size-10 items-center justify-center rounded-o text-chu-phu hover:bg-neutral-nen lg:hidden"
        >
          <Menu size={20} aria-hidden="true" />
        </button>

        {/*
          Thu gọn thanh bên — chỉ có ở khổ có thanh bên cố định (`lg:`). Dưới
          khổ đó menu đã là ngăn kéo, thu gọn thêm thì không còn gì để thu.
        */}
        {onDoiMenuGon && (
          <button
            type="button"
            onClick={onDoiMenuGon}
            aria-label={menuGon ? 'Mở rộng menu' : 'Thu gọn menu'}
            title={menuGon ? 'Mở rộng menu' : 'Thu gọn menu'}
            aria-pressed={menuGon}
            className="vung-bam -ml-2 hidden size-10 items-center justify-center rounded-o text-chu-phu hover:bg-neutral-nen lg:inline-flex"
          >
            {menuGon ? (
              <PanelLeftOpen size={19} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={19} aria-hidden="true" />
            )}
          </button>
        )}

        <nav aria-label="Đường dẫn" className="min-w-0 flex-1">
          <ol className="flex min-w-0 flex-wrap items-center gap-1.5 text-phu text-chu-phu">
            {duongDan.map((phan, i) => (
              <li key={phan} className="flex min-w-0 items-center gap-1.5">
                {i > 0 && (
                  <span aria-hidden="true" className="text-vien">
                    /
                  </span>
                )}
                <span
                  className={
                    i === duongDan.length - 1 ? 'truncate font-semibold text-chu' : 'truncate'
                  }
                >
                  {phan}
                </span>
              </li>
            ))}
          </ol>
        </nav>

        {/*
          `min-w-0` cho cả hàng và cho ô kỳ: bề ngang tự nhiên của `<select>`
          bằng **mục dài nhất** trong nó (~400px với menu kỳ đầy đủ), nên thiếu
          nó là thanh trên đẩy cả trang tràn ngang ở khổ 390 (bài `e2e/91`).
        */}
        <div className="flex w-full min-w-0 items-center gap-2 md:w-auto">
          <TimKiemChung />
          <div className="min-w-0 shrink">
            <OChonKyChung />
          </div>
        </div>
      </div>
    </header>
  );
}
